/**
 * @file index.tsx
 *
 * @fileoverview Renders sorting element as a filter on the job search pages.
 * This file contains multiple components because filters have different UI on desktop or mobile devices
 * but the functionality is the same on both which is to call the "toggleFilter" prop.
 */

import React from 'react';
import { ParsedUrlQuery } from 'querystring';
import { FormattedMessage, useIntl } from 'react-intl';
import { useFrontendConfig } from '~/jobiqo/context/frontendConfig';
import { FacetPopover } from '~/vcl/molecules/07-search/02-facet/02-facet-popover';
import { FacetChildList } from '~/vcl/molecules/07-search/02-facet/01-facet-box';
import FlexItem from '~/vcl/atoms/06-layout/03-flex/02-flex-item';
import { Tab, TabPanel } from '~/vcl/atoms/17-tabs';
import { getSortingFilterOptions } from '~/jobiqo/components/JobSearch/components/SortingFilter/utils';
import Radio from '~/vcl/atoms/01-forms/14-radios/01-radio';

export interface SortingFilterProps {
  /**
   * Callback when a filter is toggled (added / removed).
   */
  toggleFilter: (filter: string, selectedItems: string[]) => void;
  /**
   * The current querystring object.
   */
  querystring: ParsedUrlQuery;
}

/**
 * Checks in the URL for the selected items.
 */
export const getSortingSelectedItems = (
  querystring: ParsedUrlQuery,
): string[] => {
  const SORTING_FILTER_OPTIONS = getSortingFilterOptions();
  return SORTING_FILTER_OPTIONS.filter((value: any) =>
    querystring?.sorting?.includes(value.code),
  ).map((item: any) => {
    return item.code;
  });
};

/**
 * Renders all the filter options as radio buttons using the checkboxes logic
 * and how they toggle the filter.
 */
export const SortingFilterRadios = ({ selectedItems, toggleFilter }) => {
  const intl = useIntl();
  const SORTING_FILTER_OPTIONS = getSortingFilterOptions(intl);
  return (
    <>
      {SORTING_FILTER_OPTIONS.map((selectableItem: any) => {
        const itemSelected =
          selectedItems.includes(selectableItem.code) ||
          (selectableItem.code === 'relevancy' && selectedItems.length === 0);
        return (
          <FacetChildList key={selectableItem.code}>
            <Radio
              title={selectableItem.name}
              id={`sorting_filter-${selectableItem.code}`}
              name="sorting_filter"
              value={selectableItem.code}
              checked={itemSelected}
              onChange={() =>
                toggleFilter(
                  'sorting',
                  itemSelected
                    ? []
                    : selectableItem.code === 'relevancy'
                    ? []
                    : [selectableItem.code],
                )
              }
            />
          </FacetChildList>
        );
      })}
    </>
  );
};

/**
 * Renders sorting element as a filter on the job search pages.
 * This file contains multiple components because filters have different UI on desktop or mobile devices
 * but the functionality is the same on both which is to call the "toggleFilter" prop.
 */
export const SortingFilter = ({
  toggleFilter,
  querystring,
}: SortingFilterProps) => {
  const { JOBS } = useFrontendConfig();
  const JOB_SEARCH_SORTING_FILTER_ENABLED =
    JOBS.JOB_SEARCH_SORTING_FILTER_ENABLED;
  if (!JOB_SEARCH_SORTING_FILTER_ENABLED) {
    return null;
  }

  const selectedItems = getSortingSelectedItems(querystring);

  return (
    <div>
      <FlexItem margin={4}>
        <FacetPopover
          label={
            <>
              <span>
                <FormattedMessage
                  id="sorting_filter_label"
                  defaultMessage="Sort by"
                />
              </span>
            </>
          }
        >
          <SortingFilterRadios
            toggleFilter={toggleFilter}
            selectedItems={selectedItems}
          />
        </FacetPopover>
      </FlexItem>
    </div>
  );
};

/**
 * Renders the tab for the mobile version of the filter.
 */
export const SortingFilterTab = ({}) => {
  const { JOBS } = useFrontendConfig();
  const JOB_SEARCH_SORTING_FILTER_ENABLED =
    JOBS.JOB_SEARCH_SORTING_FILTER_ENABLED;
  return JOB_SEARCH_SORTING_FILTER_ENABLED ? (
    <Tab value="sorting_filter">
      <>
        <span>
          <FormattedMessage
            id="sorting_filter_label"
            defaultMessage="Sort by"
          />
        </span>
      </>
    </Tab>
  ) : null;
};

/**
 * Renders Panel for the mobile version of the filter.
 */
export const SortingFilterTabPanel = ({
  querystring,
  toggleFilter,
}: SortingFilterProps) => {
  const { JOBS } = useFrontendConfig();
  const JOB_SEARCH_SORTING_FILTER_ENABLED =
    JOBS.JOB_SEARCH_SORTING_FILTER_ENABLED;
  const selectedItems = getSortingSelectedItems(querystring);
  return JOB_SEARCH_SORTING_FILTER_ENABLED ? (
    <TabPanel value="sorting_filter">
      <SortingFilterRadios
        toggleFilter={toggleFilter}
        selectedItems={selectedItems}
      />
    </TabPanel>
  ) : null;
};
